import { getPriceInNumber } from './helperFunction'
import { currentLocale } from '../i18n/current'

/**
 * Regional pricing helpers.
 *
 * The stripe-plan API now returns, per plan, a numeric `amount` and its `currency`
 * (the visitor's regional currency when Stripe carries a price for it, USD
 * otherwise), plus a top-level `pricing` context describing the detected country
 * and the regional discount baked into those amounts.
 */

export interface RegionalPricingContext {
  country: string | null
  currency: string
  discountPct: number
}

const ZERO_DECIMAL_CURRENCIES = new Set(['JPY', 'KRW', 'VND'])

// Where Intl's formatting falls back to the bare ISO code, substitute the
// symbol people actually see at home (Rp 145,999 rather than IDR 145,999).
const SYMBOL_PREFIX_OVERRIDES: Record<string, string> = {
  IDR: 'Rp ',
  THB: '฿',
  MYR: 'RM',
  ZAR: 'R',
  SGD: 'S$',
}
const SYMBOL_SUFFIX_OVERRIDES: Record<string, string> = {
  PLN: ' zł',
  NOK: ' kr',
}

/**
 * Preferred way to read a plan's numeric amount: the API's `amount` (local
 * currency), falling back to parsing the legacy `$` display string.
 */
export const getPlanAmount = (plan: { amount?: number; price?: string | number } | null | undefined): number => {
  if (typeof plan?.amount === 'number') return plan.amount
  if (typeof plan?.price === 'number') return plan.price
  return getPriceInNumber((plan?.price as string) ?? '')
}

/**
 * Format an amount in a given currency for display.
 *
 * USD keeps the historical two-decimal rendering ($15.00). Other currencies drop
 * decimals when they'd be noise: zero-decimal currencies (¥, ₩, ₫), large
 * amounts (Rp 145,999), and whole amounts (HK$88) — matching how those prices
 * are conventionally written.
 */
export const formatMoney = (amount: number, currency = 'USD', locale = currentLocale()): string => {
  const code = (currency || 'USD').toUpperCase()
  const wholeNumber =
    ZERO_DECIMAL_CURRENCIES.has(code) || Math.abs(amount) >= 1000 || Number.isInteger(Number(amount.toFixed(2)))
  const digits = code === 'USD' ? 2 : wholeNumber ? 0 : 2
  try {
    if (SYMBOL_PREFIX_OVERRIDES[code] || SYMBOL_SUFFIX_OVERRIDES[code]) {
      const number = new Intl.NumberFormat(locale, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(amount)
      return `${SYMBOL_PREFIX_OVERRIDES[code] || ''}${number}${SYMBOL_SUFFIX_OVERRIDES[code] || ''}`
    }
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(amount)
  } catch {
    return `$${amount.toFixed(2)}`
  }
}

/** 'VN' -> '🇻🇳' (regional-indicator pair; empty string for bad input). */
export const getFlagEmoji = (countryCode?: string | null): string => {
  if (!countryCode || !/^[a-zA-Z]{2}$/.test(countryCode)) return ''
  return String.fromCodePoint(
    ...countryCode
      .toUpperCase()
      .split('')
      .map((c) => 0x1f1e6 + c.charCodeAt(0) - 65),
  )
}

/** 'VN' -> 'Vietnam' (falls back to the code itself). */
export const getCountryName = (countryCode?: string | null, locale = currentLocale()): string => {
  if (!countryCode) return ''
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(countryCode.toUpperCase()) || countryCode
  } catch {
    return countryCode
  }
}

/** 'VND' -> 'Vietnamese dong' (falls back to the code itself). */
export const getCurrencyName = (currency?: string | null, locale = currentLocale()): string => {
  if (!currency) return ''
  try {
    return new Intl.DisplayNames([locale], { type: 'currency' }).of(currency.toUpperCase()) || currency
  } catch {
    return currency
  }
}
